// Cinematic clearance scan gate for the Industry transmission.
// Purely theatrical: no recognition, analysis, storage, or upload of camera data.
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ScanFace, ArrowRight } from "lucide-react";

const STATUSES = [
  "OPTICAL SIGNAL ACQUIRED",
  "SCANNING…",
  "PRESENCE CONFIRMED",
  "IDENTITY VERIFICATION: SIMULATED",
  "INDUSTRY CLEARANCE GRANTED",
];
const SCAN_MS = 6000;

export const ClearanceScan = ({ onDone }) => {
  const reduced = useReducedMotion();
  const [stage, setStage] = useState("gate"); // gate | scanning | denied | granted
  const [statusIdx, setStatusIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const timersRef = useRef([]);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const finish = () => {
    try { sessionStorage.setItem("blv_clearance_granted", "1"); } catch {}
    stopCamera();
    onDone?.();
  };

  useEffect(() => {
    if (reduced) finish();
    return () => { stopCamera(); timersRef.current.forEach(clearTimeout); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grant = () => {
    setStage("granted");
    stopCamera();
    timersRef.current.push(setTimeout(finish, 2000));
  };

  const beginScan = async () => {
    if (!navigator.mediaDevices?.getUserMedia) return setStage("denied");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = stream;
      setStage("scanning");
      requestAnimationFrame(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      });
      STATUSES.forEach((_, i) => {
        timersRef.current.push(setTimeout(() => setStatusIdx(i), (i * SCAN_MS) / STATUSES.length));
      });
      const start = Date.now();
      const tick = setInterval(() => {
        const p = Math.min(100, Math.round(((Date.now() - start) / SCAN_MS) * 100));
        setProgress(p);
        if (p >= 100) clearInterval(tick);
      }, 80);
      timersRef.current.push(setTimeout(grant, SCAN_MS + 300));
    } catch {
      setStage("denied");
    }
  };

  if (reduced) return null;

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center overflow-hidden bg-onyx" data-testid="clearance-scan">
      <div className="grain-dark pointer-events-none absolute inset-0" />
      <div className="vignette pointer-events-none absolute inset-0" />
      <AnimatePresence mode="wait">
        {stage === "gate" && (
          <motion.div key="gate" className="px-6 text-center" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
            <p className="font-mono text-[10px] uppercase tracking-[0.5em] text-gold">
              <span className="mr-2 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-gold align-middle" aria-hidden="true" />
              Industry network checkpoint
            </p>
            <h1 className="mt-5 font-display text-4xl uppercase leading-[0.95] tracking-tight text-metallic sm:text-6xl">Clearance Required</h1>
            <p className="mx-auto mt-5 max-w-sm font-body text-sm leading-relaxed text-silver/75">
              Cinematic effect only — nothing is recorded, stored, or analyzed. Camera use is completely optional.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <button onClick={beginScan} data-testid="begin-scan-btn"
                className="btn-sweep inline-flex items-center gap-2 border border-gold bg-gradient-to-b from-goldlight to-gold px-8 py-3.5 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-onyx transition-transform duration-200 hover:-translate-y-0.5">
                <ScanFace size={16} /> Begin Clearance Scan
              </button>
              <button onClick={grant} data-testid="skip-scan-btn"
                className="border border-silver/40 px-8 py-3.5 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-silver transition-colors hover:border-gold hover:text-goldlight">
                Proceed Without Scan
              </button>
            </div>
          </motion.div>
        )}

        {stage === "scanning" && (
          <motion.div key="scan" className="w-full max-w-lg px-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} data-testid="scan-interface">
            <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.3em] text-gold">
              <span><span className="mr-2 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-gold align-middle" aria-hidden="true" />BLV optical scan</span>
              <span data-testid="scan-progress">{progress}%</span>
            </div>
            <div className="relative mt-3 aspect-[4/5] w-full overflow-hidden border border-gold/40 bg-black sm:aspect-[4/3]">
              <video ref={videoRef} muted playsInline className="h-full w-full object-cover" style={{ transform: "scaleX(-1)", filter: "grayscale(0.35) brightness(0.9) contrast(1.05)" }} />
              {/* scanning grid */}
              <div className="pointer-events-none absolute inset-0 opacity-25" style={{ backgroundImage: "linear-gradient(rgba(201,162,39,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(201,162,39,0.35) 1px, transparent 1px)", backgroundSize: "36px 36px" }} />
              {/* corner brackets */}
              {["top-2 left-2 border-t-2 border-l-2", "top-2 right-2 border-t-2 border-r-2", "bottom-2 left-2 border-b-2 border-l-2", "bottom-2 right-2 border-b-2 border-r-2"].map((cls) => (
                <div key={cls} className={`pointer-events-none absolute h-8 w-8 border-gold ${cls}`} />
              ))}
              {/* targeting frame */}
              <div className="pointer-events-none absolute left-1/2 top-1/2 h-[62%] w-[52%] -translate-x-1/2 -translate-y-1/2 rounded-[50%] border border-gold/60 shadow-[0_0_30px_rgba(201,162,39,0.15)]">
                <div className="absolute inset-2 rounded-[50%] border border-gold/20" />
              </div>
              {/* laser line */}
              <motion.div className="pointer-events-none absolute inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-goldlight to-transparent shadow-[0_0_18px_rgba(231,200,115,0.8)]"
                initial={{ top: "2%" }} animate={{ top: ["2%", "96%", "2%"] }} transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }} />
              {/* subtle static flicker */}
              <motion.div className="pointer-events-none absolute inset-0 bg-gold/5" animate={{ opacity: [0, 0.12, 0, 0.06, 0] }} transition={{ duration: 1.7, repeat: Infinity }} />
              {/* terminal status */}
              <div className="absolute bottom-2 left-2 right-2 flex items-end justify-between">
                <p className="bg-onyx/70 px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-goldlight backdrop-blur-sm" data-testid="scan-status">
                  &gt; {STATUSES[statusIdx]}
                </p>
              </div>
              {/* progress bar */}
              <div className="absolute left-2 right-2 top-2 mx-10 h-[3px] bg-silver/15">
                <div className="h-full bg-gradient-to-r from-gold to-goldlight transition-[width] duration-150" style={{ width: `${progress}%` }} />
              </div>
            </div>
            <p className="mt-3 text-center font-mono text-[8px] uppercase tracking-[0.25em] text-silver/45">
              Simulated scan · no imagery is captured, stored, or transmitted
            </p>
          </motion.div>
        )}

        {stage === "denied" && (
          <motion.div key="denied" className="px-6 text-center" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} data-testid="scan-denied">
            <p className="font-mono text-[10px] uppercase tracking-[0.45em] text-silver/60">Camera access unavailable</p>
            <h2 className="mt-5 font-display text-3xl uppercase leading-[0.95] tracking-tight text-metallic sm:text-5xl">Manual Clearance<br />Authorized</h2>
            <p className="mx-auto mt-4 max-w-sm font-body text-sm leading-relaxed text-silver/75">The Architect vouches for you this time.</p>
            <button onClick={grant} data-testid="proceed-to-briefing-btn"
              className="btn-sweep mt-8 inline-flex items-center gap-2 border border-gold bg-gradient-to-b from-goldlight to-gold px-8 py-3.5 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-onyx transition-transform duration-200 hover:-translate-y-0.5">
              Proceed to Briefing <ArrowRight size={15} />
            </button>
          </motion.div>
        )}

        {stage === "granted" && (
          <motion.div key="granted" className="px-6 text-center" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} data-testid="clearance-granted">
            <motion.div className="mx-auto inline-block -rotate-2 border-2 border-gold px-6 py-2.5 shadow-[0_0_40px_rgba(201,162,39,0.25)]"
              initial={{ scale: 1.15, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.35 }}>
              <span className="border border-gold/40 px-3 py-1 font-display text-3xl uppercase tracking-[0.15em] text-gold sm:text-4xl">Clearance Granted</span>
            </motion.div>
            <motion.p className="mt-6 font-cinzel text-xs uppercase tracking-[0.35em] text-cream/90 sm:text-sm"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
              Welcome to the Industry Network
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
